import { memo, useCallback } from 'react';

import { TreeItem, TreeItemButton } from '@air/component-tree';
import { Button } from '@air/primitive-button';

import { LocationsFilterBoardTree } from '~/components/Filters/LocationsFilter/LocationsFilterBoardTree';
import { type LocationsFilterBoardTreeItemProps } from '~/components/Filters/LocationsFilter/LocationsFilterBoardTreeItem';
import { type LocationsFilterBoard } from '~/components/Filters/LocationsFilter/useLocationsFilterData';
import { useGeneralLibraryTitle } from '~/components/LibraryBeta/hooks/useGeneralLibraryTitle';
import { GeneralLibraryIcon } from '~/components/LibraryBeta/LibraryNavigation/components/LibraryNavigationGeneralItem/GeneralLibraryIcon';
import { useLocationsFilterStore } from '~/components/v2/store/features/filters/useLocationsFilterStore';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useWorkspaceBoards } from '~/swr-hooks/boards/useWorkspaceBoards';

export type LocationsFilterLibraryGeneralTreeItemProps = Pick<LocationsFilterBoardTreeItemProps, 'onBoardSelect'> & {
  /**
   * Pre-fetched boards from search, if provided will skip lazy fetch
   */
  boards?: LocationsFilterBoard[];
};

export const LocationsFilterLibraryGeneralTreeItem = memo(
  ({ boards: prefetchedBoards = [], onBoardSelect }: LocationsFilterLibraryGeneralTreeItemProps) => {
    const { currentWorkspace } = useCurrentWorkspace();
    const { generalLibraryTitle } = useGeneralLibraryTitle();
    const {
      data: fetchedBoards,
      isLoadingMore,
      hasMore,
      isInitialLoading,
      loadNextPage,
    } = useWorkspaceBoards({
      workspaceId: prefetchedBoards.length > 0 ? undefined : currentWorkspace?.id,
      view: 'root',
    });

    const displayBoards = prefetchedBoards.length > 0 ? prefetchedBoards : fetchedBoards;

    const storeTreeItem = useLocationsFilterStore((store) => store.getTreeItem({ id: 'general' }));
    // When boards are pre-fetched (search mode), default to expanded; otherwise use store value
    const open = prefetchedBoards.length > 0 ? (storeTreeItem?.open ?? true) : !!storeTreeItem?.open;
    const setTreeItemOpen = useLocationsFilterStore((store) => store.setTreeItem);

    const shouldShowBoards = open && !!displayBoards.length;

    const onOpenChange = useCallback(
      (open: boolean) => {
        setTreeItemOpen({ id: 'general', open, touched: true });
      },
      [setTreeItemOpen],
    );

    const onClick = useCallback(() => {
      onOpenChange(!open);
    }, [onOpenChange, open]);

    const handleLoadMore = useCallback(() => {
      loadNextPage();
    }, [loadNextPage]);

    return (
      <>
        <TreeItem
          data-library-id={generalLibraryTitle}
          onOpenChange={onOpenChange}
          open={open}
          prefix={<GeneralLibraryIcon />}
        >
          <TreeItemButton onClick={onClick}>{generalLibraryTitle}</TreeItemButton>
        </TreeItem>

        {shouldShowBoards && (
          <div className="ml-[28px]">
            <LocationsFilterBoardTree onBoardSelect={onBoardSelect} boards={displayBoards} />

            {!prefetchedBoards.length && hasMore && !isInitialLoading && (
              <Button
                appearance="ghost"
                color="grey"
                data-testid="LOCATIONS_FILTER_SHOW_MORE"
                onClick={handleLoadMore}
                size="small"
              >
                {isLoadingMore ? 'Loading...' : 'Show more'}
              </Button>
            )}
          </div>
        )}
      </>
    );
  },
);

LocationsFilterLibraryGeneralTreeItem.displayName = 'LocationsFilterLibraryGeneralTreeItem';
